<template>
  <div
    :class="cardClasses"
    :role="clickable ? 'button' : undefined"
    :tabindex="clickable ? 0 : undefined"
    @click="handleClick"
    @keydown.enter.self="handleClick"
    @keydown.space.self.prevent="handleClick"
  >
    <slot />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

/**
 * AppCard — mirrors core/ui/cards/AppCard.kt.
 *
 *   outlined (default) — surface + 1px hairline, no shadow
 *   raised             — surface + soft shadow (sheets, popovers, dropdowns)
 *   widget             — larger radius + soft shadow (container holding rows)
 *
 * Ink design system uses hairline outlines almost everywhere. Shadow is the
 * exception — reserved for raised surfaces like dropdowns and modals.
 */

interface Props {
  /** `elevated` is a legacy alias for `raised` — kept until call sites migrate. */
  variant?: 'outlined' | 'raised' | 'widget' | 'elevated'
  hoverable?: boolean
  clickable?: boolean
  padding?: 'none' | 'sm' | 'md' | 'lg'
  /**
   * Selected — paints a 3px coral leading bar (matches Android selected
   * list row treatment). Use sparingly; coral is brand-scarce.
   */
  selected?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'outlined',
  hoverable: false,
  clickable: false,
  padding: 'none',
  selected: false,
})

const emit = defineEmits<{
  click: [event: MouseEvent | KeyboardEvent]
}>()

const cardClasses = computed(() => [
  'app-card',
  `app-card-${props.variant}`,
  `app-card-padding-${props.padding}`,
  {
    'app-card-hoverable': props.hoverable,
    'app-card-clickable': props.clickable,
    'app-card-selected': props.selected,
  },
])

const handleClick = (event: MouseEvent | KeyboardEvent) => {
  if (props.clickable) {
    emit('click', event)
  }
}
</script>

<style scoped>
.app-card {
  position: relative;
  background: var(--surface);
  transition:
    background-color var(--motion-fast),
    border-color var(--motion-fast),
    box-shadow var(--motion-fast);
}

/* Outlined — default ink-system look (hairline border, no shadow). */
.app-card-outlined {
  border: var(--hairline-border);
  border-radius: var(--radius-lg);
}
.app-card-outlined.app-card-hoverable:hover {
  border-color: var(--outline);
  background: var(--surface-muted);
}

/* Raised — popovers, sheets, dropdowns. */
.app-card-raised,
.app-card-elevated {
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-raise);
}
.app-card-raised.app-card-hoverable:hover,
.app-card-elevated.app-card-hoverable:hover {
  box-shadow: var(--shadow-modal);
}

/* Widget — container that holds rows of items. Larger radius. */
.app-card-widget {
  border: var(--hairline-border);
  border-radius: var(--radius-xl);
  background: var(--surface);
}

/* Selected — coral leading bar (3px). Brand accent. */
.app-card-selected {
  background: var(--coral-soft);
}
.app-card-selected::before {
  content: '';
  position: absolute;
  inset: 0 auto 0 0;
  width: 3px;
  border-top-left-radius: inherit;
  border-bottom-left-radius: inherit;
  background: var(--coral);
}

/* Clickable */
.app-card-clickable {
  cursor: pointer;
  user-select: none;
}
.app-card-clickable:focus-visible {
  outline: 2px solid var(--focus-ring);
  outline-offset: 2px;
}
.app-card-clickable:active {
  transform: scale(0.997);
}

/* Padding — matches Android scaled to web pixels. */
.app-card-padding-none { padding: 0; }
.app-card-padding-sm   { padding: var(--space-12); }
.app-card-padding-md   { padding: var(--space-16); }
.app-card-padding-lg   { padding: var(--space-20); }
</style>
